import HowToRegIcon from "@mui/icons-material/HowToReg";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type AHDate from "_shared/dates/date";
import type Guid from "_shared/guids/guid";
import AccessRequest from "permissions/access-requests/access-request";
import { AccessRequestStatus } from "permissions/access-requests/_schemas/access-request-schema";
import {
  ACCESS_REQUEST_NOTE_MAX_LENGTH,
  useApproveAccessRequestMutation,
  useDenyAccessRequestMutation,
  useGetAccessRequestsQuery,
  useGetMyAccessRequestsQuery,
} from "permissions/access-requests/_api/access-requests-api";
import { ACCESS_REQUEST_PARAMETER, isInAppPath } from "permissions/access-requests/access-requests-paths";
import PermissionsSubjectHeader from "permissions/_views/scaffold/permissions-subject-header";
import useSessionUser from "permissions/session/use-session-user";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router";

export const OWN_REQUEST_REASON = "You cannot decide your own request.";
export const ACCESS_NOT_HELD_REASON = "You do not hold this access, so you cannot grant it.";

function day(date: AHDate | undefined): string {
  return date ? date.toString("MMM D, YYYY") : "";
}

/** Why the reader may not decide a request the API says they may not, in their terms. */
export function undecidableReason(request: AccessRequest, readerId: Guid | undefined): string {
  return readerId && request.requesterUserId.isEqualTo(readerId) ? OWN_REQUEST_REASON : ACCESS_NOT_HELD_REASON;
}

function outcome(request: AccessRequest): string {
  if (request.status === AccessRequestStatus.Approved) return "Approved";
  if (request.status === AccessRequestStatus.Denied) return "Denied";
  return "Waiting";
}

function Section(props: Readonly<{ id: string; title: string; children: ReactNode }>) {
  return (
    <Card variant="outlined" component="section" aria-labelledby={`${props.id}-title`}>
      <CardContent sx={{ display: "grid", gap: 1.5 }}>
        <Typography component="h2" variant="h6" id={`${props.id}-title`}>
          {props.title}
        </Typography>
        {props.children}
      </CardContent>
    </Card>
  );
}

function RequestList(props: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Box component="ul" aria-label={props.label} sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1 }}>
      {props.children}
    </Box>
  );
}

/** One request: who asked, for what, why, when, and the page they asked from. */
function RequestRow(
  props: Readonly<{ request: AccessRequest; highlighted: boolean; showRequester: boolean; children?: ReactNode }>,
) {
  const { request, highlighted, showRequester } = props;
  const rowId = `access-request-${request.id.value}`;

  useEffect(() => {
    if (highlighted) document.getElementById(rowId)?.scrollIntoView?.({ block: "center" });
  }, [highlighted, rowId]);

  return (
    <Box
      component="li"
      id={rowId}
      aria-current={highlighted ? "true" : undefined}
      sx={{
        border: 1,
        borderColor: highlighted ? "primary.main" : "divider",
        borderRadius: 1,
        p: 1.5,
        display: "grid",
        gap: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
        {showRequester
          ? `${request.requesterName} asked for access to ${request.accessDescription}`
          : `Access to ${request.accessDescription}`}
      </Typography>
      {request.context && (
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {request.context}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" component="p">
        {`Asked ${day(request.createdDate)}`}
        {isInAppPath(request.returnPath) && (
          <>
            {" · "}
            <Link component={RouterLink} to={request.returnPath}>
              Open the page it was asked from
            </Link>
          </>
        )}
      </Typography>
      {!request.isPending && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={outcome(request)}
            color={request.status === AccessRequestStatus.Approved ? "success" : "default"}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            {[request.decidedByName ? `by ${request.decidedByName}` : "", request.decidedDate ? `on ${day(request.decidedDate)}` : ""]
              .filter(Boolean)
              .join(" ")}
          </Typography>
        </Box>
      )}
      {request.decisionNote && (
        <Typography variant="body2" sx={{ fontStyle: "italic", overflowWrap: "anywhere" }}>
          {`“${request.decisionNote}”`}
        </Typography>
      )}
      {props.children}
    </Box>
  );
}

/** Approve and Deny, or why the reader may do neither. */
function DecisionControls(
  props: Readonly<{
    request: AccessRequest;
    readerId: Guid | undefined;
    busy: boolean;
    onApprove: (request: AccessRequest) => void;
    onDeny: (request: AccessRequest) => void;
  }>,
) {
  const { request, readerId, busy, onApprove, onDeny } = props;
  const reasonId = `access-request-${request.id.value}-reason`;
  const reason = request.callerMayDecide ? undefined : undecidableReason(request, readerId);
  const name = `${request.requesterName}'s request for access to ${request.accessDescription}`;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
      <Button
        size="small"
        variant="contained"
        disabled={!request.callerMayDecide || busy}
        aria-label={`Approve ${name}`}
        aria-describedby={reason ? reasonId : undefined}
        onClick={() => onApprove(request)}
      >
        Approve
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={!request.callerMayDecide || busy}
        aria-label={`Deny ${name}`}
        aria-describedby={reason ? reasonId : undefined}
        onClick={() => onDeny(request)}
      >
        Deny
      </Button>
      {reason && (
        <Typography id={reasonId} variant="caption" color="text.secondary">
          {reason}
        </Typography>
      )}
    </Box>
  );
}

function loadState(error: unknown, isLoading: boolean, what: string): ReactNode {
  if (error) return <Alert severity="error">{`Unable to load ${what}. ${getErrorMessageFromUnknown(error) ?? ""}`}</Alert>;
  if (isLoading) return <Skeleton variant="rectangular" height={72} />;
  return undefined;
}

/**
 * The organization's access requests, for a member who may grant them (#6596): the ones waiting,
 * oldest first, each with Approve and Deny, and the ones decided, newest first.
 */
function OrganizationRequests(props: Readonly<{ readerId: Guid | undefined; highlightedId: string | null }>) {
  const { readerId, highlightedId } = props;
  const answer = useGetAccessRequestsQuery();
  const [approve, approving] = useApproveAccessRequestMutation();
  const [deny, denying] = useDenyAccessRequestMutation();
  const [denyingRequest, setDenyingRequest] = useState<AccessRequest>();
  const [note, setNote] = useState("");
  const requests = useMemo(() => AccessRequest.fromJSONArray(answer.data), [answer.data]);
  const pending = requests.filter((request) => request.isPending);
  const decided = requests.filter((request) => !request.isPending);
  const busy = approving.isLoading || denying.isLoading;

  async function decide(request: AccessRequest, approved: boolean, decisionNote?: string) {
    const send = approved ? approve : deny;
    const failure = await send({ accessRequestId: request.id.value, note: decisionNote })
      .unwrap()
      .then(() => undefined as unknown)
      .catch((error: unknown) => error ?? new Error());
    if (failure === undefined) {
      enqueueSnackbar(`${approved ? "Approved" : "Denied"} ${request.requesterName}'s request.`, {
        variant: "success",
        role: "status",
      });
      return;
    }
    showErrorSnackbar(failure, { doing: "deciding the request", operation: "permissions.access-requests.decide" });
  }

  function openDeny(request: AccessRequest) {
    setNote("");
    setDenyingRequest(request);
  }

  function confirmDeny() {
    const request = denyingRequest;
    setDenyingRequest(undefined);
    if (request) void decide(request, false, note);
  }

  const state = loadState(answer.error, answer.isLoading, "the access requests");
  return (
    <>
      <Section id="waiting" title="Waiting for a decision">
        {state ??
          (pending.length === 0 ? (
            <Typography color="text.secondary">Nobody is waiting for access.</Typography>
          ) : (
            <RequestList label="Waiting for a decision">
              {pending.map((request) => (
                <RequestRow
                  key={request.id.value}
                  request={request}
                  highlighted={request.id.value === highlightedId}
                  showRequester
                >
                  <DecisionControls
                    request={request}
                    readerId={readerId}
                    busy={busy}
                    onApprove={(chosen) => void decide(chosen, true)}
                    onDeny={openDeny}
                  />
                </RequestRow>
              ))}
            </RequestList>
          ))}
      </Section>
      <Section id="history" title="History">
        {state ??
          (decided.length === 0 ? (
            <Typography color="text.secondary">No request has been decided yet.</Typography>
          ) : (
            <RequestList label="History">
              {decided.map((request) => (
                <RequestRow
                  key={request.id.value}
                  request={request}
                  highlighted={request.id.value === highlightedId}
                  showRequester
                />
              ))}
            </RequestList>
          ))}
      </Section>
      <Dialog
        open={Boolean(denyingRequest)}
        onClose={() => setDenyingRequest(undefined)}
        aria-labelledby="deny-access-request-title"
      >
        <DialogTitle id="deny-access-request-title">Deny the request</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2 }}>
          <DialogContentText>
            {denyingRequest
              ? `${denyingRequest.requesterName} will be told you denied access to ${denyingRequest.accessDescription}.`
              : ""}
          </DialogContentText>
          <TextField
            label="Note (optional)"
            helperText="Sent with the decision."
            multiline
            minRows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            slotProps={{ htmlInput: { maxLength: ACCESS_REQUEST_NOTE_MAX_LENGTH } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyingRequest(undefined)}>Cancel</Button>
          <Button variant="contained" onClick={confirmDeny}>
            Deny
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** The reader's own requests, newest first, with how each was decided. */
function MyRequests(props: Readonly<{ highlightedId: string | null }>) {
  const answer = useGetMyAccessRequestsQuery();
  const requests = useMemo(() => AccessRequest.fromJSONArray(answer.data), [answer.data]);
  const state = loadState(answer.error, answer.isLoading, "your requests");
  return (
    <Section id="mine" title="Your requests">
      {state ??
        (requests.length === 0 ? (
          <Typography color="text.secondary">You have not asked for any access.</Typography>
        ) : (
          <RequestList label="Your requests">
            {requests.map((request) => (
              <RequestRow
                key={request.id.value}
                request={request}
                highlighted={request.id.value === props.highlightedId}
                showRequester={false}
              />
            ))}
          </RequestList>
        ))}
    </Section>
  );
}

/**
 * The Permissions page's Requests, at `/permissions/requests` (#6596).
 *
 * A member who may grant access sees the organization's requests, waiting and decided, and answers
 * them. Every member sees their own, so the notification that tells a requester how theirs went opens
 * somewhere they may read. A notification names its request in the query string, and the page marks
 * that row.
 */
export default function AccessRequestsPage() {
  const sessionUser = useSessionUser();
  const [searchParams] = useSearchParams();
  const highlightedId = searchParams.get(ACCESS_REQUEST_PARAMETER);

  if (!sessionUser) return null;
  const mayDecide = sessionUser.canDecideAccessRequests();

  return (
    <Box sx={{ height: "100%", minHeight: 0, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 }, display: "grid", gap: 2 }}>
        <PermissionsSubjectHeader
          trail={[{ label: "Permissions", to: "/permissions" }]}
          title="Requests"
          avatar={<HowToRegIcon color="action" sx={{ fontSize: 40 }} />}
          secondary={
            mayDecide
              ? "Access members of your organization asked for. Approving grants exactly what was asked for."
              : "Access you asked for, and how each request was decided."
          }
        />
        {mayDecide && <OrganizationRequests readerId={sessionUser.id} highlightedId={highlightedId} />}
        <MyRequests highlightedId={highlightedId} />
      </Box>
    </Box>
  );
}
